import { setHtml } from './http';

// Live filtering for the list pages: the filter card's form re-runs the search
// as you type (debounced) or change a control, and the results swap in place
// without a reload. Sorting and paging links inside the results do the same.
//
// Markup (see Views/Sector/Index.cshtml for the simplest case):
//   <div class="card list-card" x-data="liveList">
//     <form class="filter-bar" x-ref="form" method="get" action="…/pagingsortingsearch"> … </form>
//     <div data-live-results> table and pager </div>
//   </div>
//
// GET forms put the filters in the address bar, so refresh, Back and sharing
// keep them. A POST form (lists whose status filter only exists on the POST
// action) still filters live; the address just doesn't change.
//
// Search inputs carry data-live-search. Controls with data-live-ignore aren't
// sent (e.g. the date preset select, which only fills in the real date fields).
// A radio group with data-live-action picks the endpoint instead of being sent
// (Recruiters' "All recruiters" / "Hiring now").

const DEBOUNCE = 300;

function sameOriginLink(event, link) {
  return link && !event.defaultPrevented && event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey
    && link.origin === window.location.origin;
}

export default () => ({
  loading: false,
  timer: null,
  controller: null,

  init() {
    const form = this.$refs.form;

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.load();
    });

    form.addEventListener('input', (event) => this.changed(event));

    // Filter components inside the form (e.g. dateRange) ask for a reload.
    form.addEventListener('live-filter', () => this.load());
    form.addEventListener('change', (event) => {
      // Text inputs already reacted to `input`.
      if (!event.target.matches('[data-live-search], input[type="text"], input[type="search"]')) {
        this.changed(event);
      }
    });

    // Sorting and paging links within the results.
    this.$el.addEventListener('click', (event) => {
      const link = event.target.closest('[data-live-results] a.sort-link, [data-live-results] .table-pager a[href]');
      if (sameOriginLink(event, link) && !link.getAttribute('href').startsWith('javascript:')) {
        event.preventDefault();
        this.fetch(link.href, { method: 'GET' });
      }
    });

    // "/" jumps to the search box, as in the command palette.
    window.addEventListener('keydown', (event) => {
      const typing = event.target.closest('input, textarea, select, [contenteditable]');
      if (event.key === '/' && !typing && !event.metaKey && !event.ctrlKey) {
        const search = form.querySelector('[data-live-search]');
        if (search) {
          event.preventDefault();
          search.focus();
          search.select();
        }
      }
    });

    if (this.isGet()) {
      window.addEventListener('popstate', () => this.fetch(window.location.href, { method: 'GET', remember: false }));
    }

    this.applyDefaults();
  },

  // data-live-defaults='{"ShowNonActive":"1"}' on the form: filters to use when
  // the page is opened without any (e.g. Jobs shows active jobs, not all). A GET
  // list's filters are in the address, so it only takes them without a query
  // string; a POST list's address never changes, so the view only adds the
  // attribute when the filters are unset (Applications' default sort).
  applyDefaults() {
    const form = this.$refs.form;
    if (!form.dataset.liveDefaults || (this.isGet() && window.location.search)) {
      return;
    }

    for (const [name, value] of Object.entries(JSON.parse(form.dataset.liveDefaults))) {
      const field = form.elements.namedItem(name);
      if (field instanceof RadioNodeList) {
        [...field].forEach((option) => { option.checked = option.value === String(value); });
      } else if (field) {
        field.value = value;
      }
    }

    this.load({ replace: true });
  },

  isGet() {
    return (this.$refs.form.getAttribute('method') || 'get').toLowerCase() === 'get';
  },

  changed(event) {
    if (event.target.matches('[data-live-ignore]')) {
      return;
    }

    clearTimeout(this.timer);

    // Typing replaces the history entry rather than adding one per pause.
    if (event.target.matches('[data-live-search]')) {
      this.timer = setTimeout(() => this.load({ replace: true }), DEBOUNCE);
    } else {
      this.load();
    }
  },

  // The form's current filters as a request.
  load(options = {}) {
    clearTimeout(this.timer);
    const form = this.$refs.form;
    const data = new FormData(form);

    form.querySelectorAll('[data-live-ignore][name], [data-live-action][name]').forEach((field) => data.delete(field.name));
    const action = form.querySelector('[data-live-action]:checked')?.dataset.liveAction || form.action;

    if (this.isGet()) {
      const url = new URL(action, window.location.origin);
      for (const [key, value] of data) {
        if (value !== '') {
          url.searchParams.append(key, value);
        }
      }
      this.fetch(url.href, { method: 'GET', ...options });
    } else {
      this.fetch(new URL(action, window.location.origin).href, { method: 'POST', body: data, remember: false, ...options });
    }
  },

  async fetch(href, { method, body, remember = true, replace = false }) {
    this.controller?.abort();
    this.controller = new AbortController();
    this.loading = true;

    try {
      const response = await window.fetch(href, {
        method,
        body,
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: this.controller.signal,
      });

      if (!response.ok) {
        throw new Error(`List request failed: ${response.status}`);
      }

      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      this.swap(page);

      if (remember && method === 'GET' && href !== window.location.href) {
        window.history[replace ? 'replaceState' : 'pushState'](null, '', href);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        window.showErrorMessage?.('The list couldn\'t be updated. Try again, or reload the page.');
      }
    } finally {
      this.loading = false;
    }
  },

  // Results, the heading's count, and any filter values the response changed
  // (e.g. after Back), leaving alone whatever is being typed into.
  swap(page) {
    const results = page.querySelector('[data-live-results]');
    if (results) {
      setHtml(this.$el.querySelector('[data-live-results]'), results.innerHTML);
    }

    // The heading, with its count badge.
    const title = page.querySelector('main .page-title');
    const currentTitle = document.querySelector('main .page-title');
    if (title && currentTitle) {
      currentTitle.replaceChildren(...[...title.childNodes].map((node) => document.importNode(node, true)));
    }

    // Elements outside the card that depend on the filters, e.g. the export link.
    document.querySelectorAll('[data-live-sync][id]').forEach((element) => {
      const incoming = page.getElementById(element.id);
      if (incoming) {
        element.replaceWith(document.importNode(incoming, true));
      }
    });

    const newForm = page.querySelector('[data-live-results]')?.closest('[x-data]')?.querySelector('form');
    if (!newForm) {
      return;
    }

    this.$nextTick(() => window.dispatchEvent(new CustomEvent('live-swapped')));

    for (const field of this.$refs.form.elements) {
      if (!field.name || field === document.activeElement || field.matches('[data-live-ignore]')) {
        continue;
      }

      const incoming = newForm.elements.namedItem(field.name);
      if (!incoming) {
        continue;
      }

      if (field.type === 'radio' || field.type === 'checkbox') {
        const match = incoming instanceof RadioNodeList
          ? [...incoming].find((option) => option.value === field.value)
          : incoming;
        field.checked = !!match?.checked;
      } else if (incoming.value !== undefined && field.value !== incoming.value) {
        field.value = incoming.value;
      }
    }
  },
});
